import { useContext, useEffect, useRef } from "react";
import { Autocomplete } from "@react-google-maps/api";

import SearchIcon from "~/icons/SearchIcon";
import ThemeContext from "~/contexts/ThemeContext";
import GoogleContext from "~/contexts/GoogleContext";

import styles from "./AddressPicker.module.css";

type AddressPickerProps = {
  placeholder?: string;
  autofill?: boolean;
  address: string;
  setAddress: (value: string) => void;
};

const AddressPicker: React.FC<AddressPickerProps> = ({
  placeholder = "Enter your address",
  autofill,
  address,
  setAddress,
}) => {
  const autoCompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { template } = useContext(ThemeContext);
  const { isLoaded } = useContext(GoogleContext);

  // Auto fill location
  useEffect(() => {
    if (!autofill || !isLoaded || !window.google) return;

    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;

      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode(
        { location: { lat: latitude, lng: longitude } },
        (results, status) => {
          if (status === "OK" && results && results.length > 0) {
            const result = results[0];

            setAddress(result.formatted_address || "");
          } else {
            console.error("Failed to get address by coordinates");
          }
        }
      );
    });
  }, [autofill, isLoaded, setAddress]);

  const handlePlaceChanged = () => {
    const place = autoCompleteRef.current?.getPlace();

    if (!place || !place.geometry || !place.formatted_address) return;

    setAddress(place.formatted_address);
  };

  // Styles
  const inputStyles = {
    backgroundColor: template.global.input.backgroundColour,
    borderColor: template.global.input.borderColour,
    color: template.global.input.fontColour,
    "--focus-search-select-background-colour":
      template.global.input.focusBackgroundColour,
    "--focus-search-select-border-colour":
      template.global.input.focusBorderColour,
  };

  return (
    isLoaded && (
      <div className={styles.wrapper}>
        <div className={styles.searchIconWrapper}>
          <SearchIcon />
        </div>
        <Autocomplete
          onLoad={(autocomplete) => {
            autoCompleteRef.current = autocomplete;
          }}
          onPlaceChanged={handlePlaceChanged}
        >
          <input
            type="text"
            placeholder={placeholder}
            ref={inputRef}
            className={styles.input}
            style={inputStyles}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
        </Autocomplete>
      </div>
    )
  );
};

export default AddressPicker;
